import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Toolbar from './components/Toolbar';
import LeftPanel from './components/LeftPanel';
import Canvas from './components/Canvas';
import RightPanel from './components/RightPanel';

function App() {
  // 현재 선택된 도구 상태
  const [activeTool, setActiveTool] = useState('brush');
  
  // 현재 선택된 색상 상태
  const [currentColor, setCurrentColor] = useState('#ff0000');
  
  // 줌 레벨 상태
  const [zoomLevel, setZoomLevel] = useState(1);
  
  // 그리드 표시 여부 상태
  const [showGrid, setShowGrid] = useState(false);
  
  // UI 스케일 상태
  const [uiScale, setUiScale] = useState(1);
  
  // 레이어 상태
  const [layers, setLayers] = useState([
    { id: 1, name: '레이어 1', visible: true, active: true },
    { id: 2, name: '레이어 2', visible: true, active: false },
    { id: 3, name: '배경', visible: true, active: false }
  ]);
  
  // 픽셀 데이터 상태
  const [pixelData, setPixelData] = useState({});
  
  // 작업 히스토리 상태
  const [history, setHistory] = useState([{pixelData: {}}]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const MAX_HISTORY = 100; // 최대 히스토리 저장 개수
  
  // UI 스케일 적용
  useEffect(() => {
    document.documentElement.style.setProperty('--ui-scale', uiScale);
  }, [uiScale]);
  
  // 픽셀 데이터 변경 시 히스토리에 추가
  const updatePixelData = useCallback((newPixelData) => {
    // 현재 히스토리 인덱스 이후의 기록은 삭제
    const newHistory = history.slice(0, historyIndex + 1);
    
    // 새 상태를 히스토리에 추가
    const newState = {
      pixelData: JSON.parse(JSON.stringify(newPixelData))
    };
    newHistory.push(newState);
    
    // 히스토리가 최대 개수를 초과하면 가장 오래된 항목 제거
    if (newHistory.length > MAX_HISTORY) {
      newHistory.shift();
    }
    
    // 히스토리 업데이트
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    
    // 픽셀 데이터 업데이트
    setPixelData(JSON.parse(JSON.stringify(newPixelData)));
    
    // 디버깅용 로그
    console.log('History updated:', newHistory.length, 'Current index:', newHistory.length - 1);
  }, [history, historyIndex]);
  
  // 실행 취소 핸들러
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      
      // 이전 상태로 복원
      const prevState = history[newIndex];
      if (prevState && prevState.pixelData) {
        setPixelData(JSON.parse(JSON.stringify(prevState.pixelData)));
        console.log('Undo to index:', newIndex);
      }
    }
  }, [history, historyIndex]);
  
  // 다시 실행 핸들러
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      
      // 다음 상태로 복원
      const nextState = history[newIndex];
      if (nextState && nextState.pixelData) {
        setPixelData(JSON.parse(JSON.stringify(nextState.pixelData)));
        console.log('Redo to index:', newIndex);
      }
    }
  }, [history, historyIndex]);
  
  // 도구 선택 핸들러
  const handleToolSelect = (tool) => {
    setActiveTool(tool);
  };
  
  // 색상 변경 핸들러
  const handleColorChange = (color) => {
    setCurrentColor(color);
  };
  
  // 줌 변경 핸들러
  const handleZoomChange = (newZoom) => {
    setZoomLevel(newZoom);
  };
  
  // UI 스케일 변경 핸들러
  const handleUiScaleChange = (newScale) => {
    setUiScale(newScale);
  };
  
  // 그리드 토글 핸들러
  const handleGridToggle = () => {
    setShowGrid(!showGrid);
  };
  
  // 레이어 관련 핸들러들
  const handleLayerVisibilityToggle = (layerId) => {
    setLayers(layers.map(layer => 
      layer.id === layerId ? { ...layer, visible: !layer.visible } : layer
    ));
  };
  
  const handleLayerSelect = (layerId) => {
    setLayers(layers.map(layer => 
      ({ ...layer, active: layer.id === layerId })
    ));
  };
  
  const handleAddLayer = () => {
    const newId = Math.max(...layers.map(l => l.id)) + 1;
    setLayers([
      ...layers,
      { id: newId, name: `레이어 ${newId}`, visible: true, active: false }
    ]);
  };
  
  const handleRemoveLayer = () => {
    if (layers.length <= 1) return;
    const activeLayer = layers.find(l => l.active);
    if (activeLayer) {
      const newLayers = layers.filter(l => l.id !== activeLayer.id);
      if (newLayers.length > 0) {
        newLayers[0].active = true;
      }
      setLayers(newLayers);
    }
  };

  // 키보드 단축키 이벤트 리스너
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z: 실행 취소
      if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl+Y: 다시 실행
      else if (e.ctrlKey && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleUndo, handleRedo]);

  return (
    <div className="app">
      <Toolbar 
        onUndo={handleUndo}
        onRedo={handleRedo}
        onGridToggle={handleGridToggle}
        showGrid={showGrid}
        zoomLevel={zoomLevel}
        onZoomIn={() => handleZoomChange(Math.min(zoomLevel * 1.25, 5))}
        onZoomOut={() => handleZoomChange(Math.max(zoomLevel * 0.8, 0.25))}
        uiScale={uiScale}
        onUiScaleChange={handleUiScaleChange}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
      />
      <div className="main-container">
        <LeftPanel 
          activeTool={activeTool}
          onToolSelect={handleToolSelect}
        />
        <Canvas 
          activeTool={activeTool}
          currentColor={currentColor}
          zoomLevel={zoomLevel}
          showGrid={showGrid}
          layers={layers}
          onColorPick={handleColorChange}
          pixelData={pixelData}
          onPixelDataChange={updatePixelData}
        />
        <RightPanel 
          layers={layers}
          currentColor={currentColor}
          onColorChange={handleColorChange}
          onLayerVisibilityToggle={handleLayerVisibilityToggle}
          onLayerSelect={handleLayerSelect}
          onAddLayer={handleAddLayer}
          onRemoveLayer={handleRemoveLayer}
        />
      </div>
    </div>
  );
}

export default App;